import React from 'react'
import _ from 'lodash'
import moment from 'moment'
import { FiCheckCircle } from 'react-icons/fi'
import { useConnectionClick } from '../Integrations/components/hooks'
import { useDispatch, useSelector } from 'react-redux'
import { selectRootAccounts, selectRootAdmin, selectRootUser } from '../../rootSlice'
import ConnectButton from '../Integrations/components/ConnectButton'
import ConnectionProgress from './ConnectionProgress'

// A connected channel must not show the same "Connect" call to action as an
// unconnected one — that reads as unfinished and merchants re-run OAuth on stores
// they already linked. "Add another" stays available for a second store.
function ChannelConnectAction({ connected, channelId, onConnectClick }) {
  if (!connected) return <ConnectButton onClick={onConnectClick} channelId={channelId} />

  return (
    <div className="tw-flex tw-items-center tw-space-x-3">
      <div className="tw-flex tw-items-center tw-space-x-1.5 tw-text-sm tw-font-medium tw-text-green-600">
        <FiCheckCircle />
        <span>Connected</span>
      </div>
      <button
        type="button"
        onClick={onConnectClick}
        className="tw-text-xs tw-text-slate-500 hover:tw-text-blue-600 tw-underline tw-underline-offset-2"
      >
        Add another
      </button>
    </div>
  )
}

export default function ChannelItem({ accounts, integration, backfills }) {
  const user = useSelector(selectRootUser)
  const {
    id: channelId,
    channel,
    title,
    description,
    logo,
    enabled: channelEnabled,
    backfillEnabled,
    settingsEnabled,
    publicKey,
    connected,
  } = integration

  const filteredAccounts = accounts.filter((_account) => {
    if (_account.channel !== channel) return false

    // Filter out accounts that already have running backfills
    const runningBackillExists = !!backfills.find((_backfill) => _account.merchant_id === _backfill.merchant_id)
    return !runningBackillExists
  })

  /**
   *
   * Handle connect click
   *
   */
  const onConnectClick = useConnectionClick({ user, integration })

  return (
    <div className="tw-px-4 tw-py-4 md:tw-py-3 tw-border-b tw-border-slate-200">
      {/* MOBILE VIEW */}
      <div className="tw-block md:tw-hidden">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
          <div className="tw-w-24 tw-h-12 md:tw-w-20 md:tw-h-auto tw-flex tw-items-center tw-justify-start">{logo}</div>
          <ChannelConnectAction connected={connected} channelId={channelId} onConnectClick={onConnectClick} />
        </div>
        <div className="tw-text-sm tw-text-slate-500">{description}</div>
      </div>
      {/* END: MOBILE VIEW */}

      {/* DESKTOP VIEW */}
      <div className="tw-hidden md:tw-block">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-space-x-3">
            <div className="tw-shrink-0 tw-w-20 tw-h-8 tw-flex tw-items-center tw-justify-center">{logo}</div>
            <div className="tw-text-sm tw-text-slate-500 tw-pr-6">{description}</div>
          </div>
          <ChannelConnectAction connected={connected} channelId={channelId} onConnectClick={onConnectClick} />
        </div>
      </div>
      {/* END: DESKTOP VIEW */}

      {filteredAccounts.map((_account, i) => {
        return (
          <div key={i} className="tw-mt-2 -tw-mb-2">
            <ConnectionProgress
              isLoading={false}
              label={_account.merchant_id}
              current={null}
              total={null}
              percentage={100}
              timeRemaining={null}
            />
          </div>
        )
      })}

      {backfills.map((backfill, i) => {
        const {
          status = 'running',
          current,
          total = 1,
          merchant_id: merchantId,
          channel: backfillChannel,
          email,
          created_at = new Date(),
          updated_at,
          start_date,
          end_date,
          time_remaining: timeRemaining,
        } = backfill

        if (backfillChannel !== channel) return null

        const percentage = parseInt((current / total) * 100)
        const _timeRemaining =
          timeRemaining === -1 || timeRemaining === 0 || _.isNil(timeRemaining)
            ? null
            : timeRemaining < 60
            ? `Done in ${timeRemaining}s`
            : `Done ${moment().add(timeRemaining, 'seconds').fromNow()}`

        return (
          <div key={i} className="tw-mt-2 -tw-mb-2">
            <ConnectionProgress
              isLoading={true}
              label={merchantId}
              current={current}
              total={total}
              percentage={percentage}
              timeRemaining={_timeRemaining}
            />
          </div>
        )
      })}
    </div>
  )
}
